import { LocalNotifications, type ScheduleOptions } from '@capacitor/local-notifications';
import { Capacitor } from '@capacitor/core';
import { userSettings } from '../stores/settings';
import { get } from 'svelte/store';

/**
 * Notification service for managing local push notifications
 * Handles daily reminders for vocabulary practice
 */

export const notificationService = {
  /**
   * Request permission to show notifications
   * Required before scheduling any notifications
   */
  async requestPermission(): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) {
      console.log('Notifications only available on native platforms');
      return false;
    }

    try {
      const result = await LocalNotifications.requestPermissions();
      return result.display === 'granted';
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  },

  /**
   * Check if notification permission is granted
   */
  async checkPermission(): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) {
      return false;
    }

    try {
      const result = await LocalNotifications.checkPermissions();
      return result.display === 'granted';
    } catch (error) {
      console.error('Error checking notification permission:', error);
      return false;
    }
  },

  /**
   * Schedule a daily reminder notification
   * @param hour - Hour of day (0-23)
   * @param minute - Minute of hour (0-59)
   * @param message - Optional custom message
   */
  async scheduleDailyReminder(
    hour: number = 20,
    minute: number = 0,
    message?: string,
  ): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) {
      console.log('Cannot schedule notifications on web platform');
      return false;
    }

    const settings = get(userSettings);
    if (!settings.notifications_enabled) {
      console.log('Notifications are disabled in settings');
      return false;
    }

    const hasPermission = await this.checkPermission();
    if (!hasPermission) {
      const granted = await this.requestPermission();
      if (!granted) {
        console.log('Notification permission not granted');
        return false;
      }
    }

    try {
      // Cancel any existing daily reminder
      await this.cancelDailyReminder();

      const notificationMessage =
        message || 'Čas na procvičení slovíček! 📚 Otevři aplikaci a procvič si karty.';

      // Create schedule for daily notification
      const schedule: ScheduleOptions = {
        notifications: [
          {
            title: 'Slovíčkový trenažér',
            body: notificationMessage,
            id: 1, // Use ID 1 for daily reminder
            schedule: {
              on: {
                hour,
                minute,
              },
              every: 'day',
              allowWhileIdle: true,
            },
            sound: undefined,
            attachments: undefined,
            actionTypeId: '',
            extra: {
              type: 'daily_reminder',
            },
          },
        ],
      };

      await LocalNotifications.schedule(schedule);
      console.log(`Daily reminder scheduled for ${hour}:${minute.toString().padStart(2, '0')}`);
      return true;
    } catch (error) {
      console.error('Error scheduling daily reminder:', error);
      return false;
    }
  },

  /**
   * Cancel the daily reminder notification
   */
  async cancelDailyReminder(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    try {
      const pending = await LocalNotifications.getPending();
      const dailyReminder = pending.notifications.find((n) => n.id === 1);

      if (dailyReminder) {
        await LocalNotifications.cancel({ notifications: [{ id: 1 }] });
        console.log('Daily reminder cancelled');
      }
    } catch (error) {
      console.error('Error canceling daily reminder:', error);
    }
  },

  /**
   * Schedule a one-time notification
   * @param title - Notification title
   * @param message - Notification message
   * @param delayInMinutes - Minutes until notification shows
   */
  async scheduleOneTime(
    title: string,
    message: string,
    delayInMinutes: number = 5,
  ): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) {
      return false;
    }

    const hasPermission = await this.checkPermission();
    if (!hasPermission) {
      const granted = await this.requestPermission();
      if (!granted) {
        return false;
      }
    }

    try {
      const notificationTime = new Date();
      notificationTime.setMinutes(notificationTime.getMinutes() + delayInMinutes);

      const schedule: ScheduleOptions = {
        notifications: [
          {
            title,
            body: message,
            id: Date.now(), // Use timestamp for unique ID
            schedule: {
              at: notificationTime,
              allowWhileIdle: true,
            },
            sound: undefined,
            attachments: undefined,
            actionTypeId: '',
            extra: {
              type: 'one_time',
            },
          },
        ],
      };

      await LocalNotifications.schedule(schedule);
      console.log(`One-time notification scheduled for ${notificationTime.toLocaleString()}`);
      return true;
    } catch (error) {
      console.error('Error scheduling one-time notification:', error);
      return false;
    }
  },

  /**
   * Send an immediate notification
   * @param title - Notification title
   * @param message - Notification message
   */
  async sendNow(title: string, message: string): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) {
      console.log('Immediate notifications only available on native platforms');
      return false;
    }

    const hasPermission = await this.checkPermission();
    if (!hasPermission) {
      const granted = await this.requestPermission();
      if (!granted) {
        return false;
      }
    }

    try {
      const schedule: ScheduleOptions = {
        notifications: [
          {
            title,
            body: message,
            id: Date.now(),
            schedule: {
              at: new Date(Date.now() + 1000), // Schedule 1 second from now
              allowWhileIdle: true,
            },
            sound: undefined,
            attachments: undefined,
            actionTypeId: '',
          },
        ],
      };

      await LocalNotifications.schedule(schedule);
      return true;
    } catch (error) {
      console.error('Error sending immediate notification:', error);
      return false;
    }
  },

  /**
   * Get all pending (scheduled) notifications
   */
  async getPendingNotifications() {
    if (!Capacitor.isNativePlatform()) {
      return [];
    }

    try {
      const result = await LocalNotifications.getPending();
      return result.notifications;
    } catch (error) {
      console.error('Error getting pending notifications:', error);
      return [];
    }
  },

  /**
   * Cancel all pending notifications
   */
  async cancelAll(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    try {
      const pending = await LocalNotifications.getPending();
      if (pending.notifications.length > 0) {
        await LocalNotifications.cancel({ notifications: pending.notifications });
        console.log(`Cancelled ${pending.notifications.length} notifications`);
      }
    } catch (error) {
      console.error('Error canceling all notifications:', error);
    }
  },
};

/**
 * Initialize notification listeners
 * Call this once when the app starts
 */
export function initNotificationListeners() {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  // Listen for notification actions
  LocalNotifications.addListener('localNotificationActionPerformed', (notification) => {
    console.log('Notification action performed:', notification);
    // Handle notification tap - could navigate to specific screen
    // For example: navigate to flashcard viewer
  });

  // Listen for when notification is received while app is in foreground
  LocalNotifications.addListener('localNotificationReceived', (notification) => {
    console.log('Notification received in foreground:', notification);
  });
}

/**
 * Update notification schedule based on user settings
 * Call this when user changes notification settings
 */
export async function updateNotificationSchedule() {
  const settings = get(userSettings);

  if (!settings.notifications_enabled) {
    // User disabled notifications - cancel all
    await notificationService.cancelDailyReminder();
    return;
  }

  // Parse the time string (format: "HH:MM" or null)
  if (settings.daily_reminder_time) {
    const [hourStr, minuteStr] = settings.daily_reminder_time.split(':');
    const hour = parseInt(hourStr, 10);
    const minute = parseInt(minuteStr, 10);

    if (!isNaN(hour) && !isNaN(minute)) {
      await notificationService.scheduleDailyReminder(hour, minute);
    }
  } else {
    // Default time: 20:00 (8 PM)
    await notificationService.scheduleDailyReminder(20, 0);
  }
}
